import type { GrantType } from '@repo/api-codegen';
import type { EntityType } from '../enums/entityType';
import type { IMarketplaceIntegrationSpec } from '../models/marketplace';

export interface OAuth {
	base_url: string;
	scope?: string;
	redirect_uri?: string;
	response_type?: string;
	access_type?: string;
	label?: string;
	description?: string;
	group?: string;
	client_id?: string;
}

export type OptionalOAuth = OAuth;

export enum IntegrationCategory {
	WAREHOUSE = 'warehouse',
	DATABASE = 'database',
	DASHBOARD = 'dashboard',
	ORCHESTRATOR = 'orchestrator',
	COMMUNICATION = 'communication',
	TEST = 'test',
	CUSTOM = 'custom',
	STORAGE = 'storage',
}

export type IntegrationGroup =
	| 'Data warehouses'
	| 'Databases'
	| 'Data visualization'
	| 'Data pipeline'
	| 'Data quality'
	| 'Productivity'
	| 'Data transformation tools'
	| 'Data storage'
	| 'Custom';

export interface BuiltinIntegrationSpec {
	suggestWhitelist?: boolean;
	name: string;
	type: string;
	type_label?: string;
	group: IntegrationGroup;
	description: string;
	account_id?: string;
	api_key?: string;
	cookie?: string;
	src: string;
	srcDark?: string;
	helpArticle: string;
	documentationMarkdown: string;
	oauth?: OAuth;
	groupSettings?: Record<
		string,
		{
			isOAuth?: boolean;
			testable?: boolean;
			isOidc?: boolean;
		}
	>;
	encrypted_fields?: string[];
	categories: IntegrationCategory[];
	entity_type?: EntityType;
	resource_type?: EntityType;
	hidden?: boolean;
	isBeta?: boolean;
	is_demo?: boolean;
	supportsPreviews: boolean;
	supportsQueryBlocks: boolean;
	supportsSchemaConfiguration: boolean;
	supportsDataAccessRequests: boolean;
	supportsDataAccessRequestPreferences?: GrantType[];
	supportGoogleIdentityAwareProxy: boolean;
	supportsPushMetadata: boolean;
	supportsChartsTab: boolean;
	supportsUserLevelCredentials?: boolean;
	disablePullMetadata?: boolean;
	testable?: boolean;
	skipSchemaSelect?: boolean;
	teams?: string[];
	userLevelCredentialsFields?: string[];
	/** When true, show QueryPermissionsSettings (service/personal/oauth) instead of UserLevelCredentials toggle */
	supportsOAuthForUserCredentials?: boolean;
	secodaEnabled?: boolean;
	secodaCoreEnabled?: boolean;
	isExtension?: boolean;
	featureFlag?: string; // Optional feature flag to control integration visibility
}

export type SlackIntegrationSpec = BuiltinIntegrationSpec & {
	oauth: OAuth;
};

export type IntegrationSpec =
	| {
			type: 'builtin';
			value: BuiltinIntegrationSpec;
	  }
	| { type: 'marketplace'; value: IMarketplaceIntegrationSpec };

export function integrationSpecName(spec: IntegrationSpec) {
	return spec.type === 'builtin'
		? spec.value.name
		: spec.value.versions[0].name;
}

export function integrationSpecIconSrc(spec: IntegrationSpec) {
	return spec.type === 'builtin'
		? spec.value.src
		: spec.value.versions[0].icon_url;
}
